import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { adminAPI } from '../services/api';

const PreviewContext = createContext(null);

export const PreviewProvider = ({ children }) => {
  const { user } = useAuth();
  const [previewDomain, setPreviewDomain] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalStyles, setOriginalStyles] = useState(null);

  // Only allow preview mode for Drummond users
  const canPreview = user?.is_drummond_user;

  // Fetch preview data when domain changes
  useEffect(() => {
    if (!previewDomain || !canPreview) {
      setPreviewData(null);
      restoreBranding();
      return;
    }

    const fetchPreviewData = async () => {
      setLoading(true);
      try {
        const data = await adminAPI.previewPermissions(previewDomain);
        setPreviewData(data);
        applyBranding(data.branding);
      } catch (err) {
        console.error('Failed to fetch preview data:', err);
        setPreviewDomain(null);
        setPreviewData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewData();
  }, [previewDomain, canPreview]);

  const applyBranding = (branding) => {
    // Save original styles
    const original = {
      primaryColor: getComputedStyle(document.documentElement).getPropertyValue('--primary-color'),
      secondaryColor: getComputedStyle(document.documentElement).getPropertyValue('--secondary-color'),
    };
    setOriginalStyles(original);

    // Apply preview branding
    document.documentElement.style.setProperty('--primary-color', branding.primary_color);
    document.documentElement.style.setProperty('--secondary-color', branding.secondary_color);
  };

  const restoreBranding = () => {
    if (originalStyles) {
      document.documentElement.style.setProperty('--primary-color', originalStyles.primaryColor);
      document.documentElement.style.setProperty('--secondary-color', originalStyles.secondaryColor);
      setOriginalStyles(null);
    }
  };

  const enterPreview = (domain) => {
    setPreviewDomain(domain);
  };

  const exitPreview = () => {
    setPreviewDomain(null);
    setPreviewData(null);
    restoreBranding();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => restoreBranding();
  }, []);

  const value = {
    previewDomain,
    previewData,
    loading,
    canPreview,
    enterPreview,
    exitPreview,
    isPreviewMode: !!previewDomain,
  };

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
};

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};
